import { Component, Inject, OnInit, inject } from '@angular/core';
import { AbstractStepConfigComponent } from '../abstract-step-config/abstract-step-config.component';
import { AbstractStepDTO, ChangeDataEventStepDTO, ConfiguratorEtlService, ConfiguratorTableService, EtlConf, FieldTable } from '@tj/tj-dmp-lib-api';
import { ON_CANCEL_TOKEN, ON_SAVE_TOKEN, PARTNER_TOKEN, READ_ONLY_MODE_TOKEN, STEP_TOKEN, TENANT_TOKEN } from '../injection-tokens';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { parseType } from '../../filter/filter.utils';
import { Field, GroupField } from '../../filter/filter.type';
import { StepDescriptionService } from '../step-description.service';

type ChangeDataSelectOption = Field & { eventFieldForDelete?: string };

@Component({
    selector: 'dmp-change-data-event-step-config',
    templateUrl: './change-data-event-step-config.component.html',
    styleUrls: ['./change-data-event-step-config.component.scss'],
    standalone: false
})
export class ChangeDataEventStepConfigComponent extends AbstractStepConfigComponent {
  private readonly stepDescriptionService = inject(StepDescriptionService);

  changeDataEventForm: FormGroup;
  availableFields: GroupField[] = [];
  tableOptions: ChangeDataSelectOption[] = [];
  availableChangeTypes: { label: string, value: ChangeDataEventStepDTO.ChangeTypeEnum }[] = [];
  availableOrigins: { label: string, value: ChangeDataEventStepDTO.OriginEnum }[] = [];
  importEtlOptions: { label: string, value: string }[] = [];
  private allImportEtls: EtlConf[] | null = null;

  get changeDataEventStep(): ChangeDataEventStepDTO {
    return this.step as ChangeDataEventStepDTO;
  }

  get fieldsFormArray() { return this.changeDataEventForm.get('fields') as FormArray }
  get changeType() { return this.changeDataEventForm.get('changeType'); }
  get origins() { return this.changeDataEventForm.get('origins'); }
  get importEtlIds() { return this.changeDataEventForm.get('importEtlIds'); }
  get showImportEtlSelector(): boolean {
    return this.hasImportOrigin(this.origins?.value);
  }
  get currentChangeType(): ChangeDataEventStepDTO.ChangeTypeEnum {
    return this.changeType?.value || this.changeDataEventStep?.changeType || ChangeDataEventStepDTO.ChangeTypeEnum.UPDATE;
  }
  get isUpdateMode(): boolean {
    return this.currentChangeType === ChangeDataEventStepDTO.ChangeTypeEnum.UPDATE;
  }
  get selectableOptions(): GroupField[] | ChangeDataSelectOption[] {
    return this.isUpdateMode ? this.availableFields : this.tableOptions;
  }
  get fieldLabelKey(): string {
    return this.isUpdateMode
      ? 'components.workflowStepConfig.changeDataEventConfig.fields.label'
      : 'components.workflowStepConfig.changeDataEventConfig.fields.tableLabel';
  }
  get fieldPlaceholderKey(): string {
    return this.isUpdateMode
      ? 'components.workflowStepConfig.changeDataEventConfig.fields.placeholder'
      : 'components.workflowStepConfig.changeDataEventConfig.fields.tablePlaceholder';
  }
  get fieldRequiredErrorKey(): string {
    return this.isUpdateMode
      ? 'components.workflowStepConfig.changeDataEventConfig.fields.requiredError'
      : 'components.workflowStepConfig.changeDataEventConfig.fields.tableRequiredError';
  }

  constructor(
    @Inject(STEP_TOKEN) step: AbstractStepDTO,
    @Inject(TENANT_TOKEN) tenant: string,
    @Inject(PARTNER_TOKEN) partner: string,
    @Inject(READ_ONLY_MODE_TOKEN) readOnlyMode: string,
    @Inject(ON_SAVE_TOKEN) onSaveCallback: () => void,
    @Inject(ON_CANCEL_TOKEN) onCancelCallback: () => void,
    private formBuilder: FormBuilder,
    private tableService: ConfiguratorTableService,
    private etlService: ConfiguratorEtlService
  ) {
    super(step, tenant, partner, readOnlyMode, onSaveCallback, onCancelCallback);
  }

  protected initializeComponent(): void {
    this.createForm();
    this.loadAvailableChangeTypes();
    this.loadAvailableOrigins();
    this.loadAvailablesFields();
  }

  protected updateStepFromForm(): void {
    if (!this.changeDataEventStep || !this.changeDataEventForm.valid) return;

    const filteredValues = this.fieldsFormArray.value.filter(fieldValue => !!fieldValue?.field);
    const selectedChangeType = this.changeType?.value || ChangeDataEventStepDTO.ChangeTypeEnum.UPDATE;
    this.changeDataEventStep.changeType = selectedChangeType;
    this.changeDataEventStep.searchFields = filteredValues.map(fieldValue =>
      this.toFieldTableStep(fieldValue.field as ChangeDataSelectOption, selectedChangeType)
    );

    const selectedOrigins = (this.origins?.value ?? []) as ChangeDataEventStepDTO.OriginEnum[];
    this.changeDataEventStep.origins = selectedOrigins.length > 0 ? selectedOrigins : null;

    // Sans effet si aucune origine d'import n'est retenue : on ne garde pas une sélection devenue
    // invisible et donc trompeuse si l'utilisateur décoche puis recoche l'origine plus tard.
    const selectedImportEtlIds = (this.importEtlIds?.value ?? []) as string[];
    this.changeDataEventStep.importEtlIds = (this.hasImportOrigin(selectedOrigins) && selectedImportEtlIds.length > 0)
      ? selectedImportEtlIds
      : null;

    const fieldLabels = filteredValues.map(fieldValue =>
      this.toDescriptionLabel(fieldValue.field as ChangeDataSelectOption, selectedChangeType)
    );
    // Le libellé de l'étape est ce qui distingue deux déclencheurs sur la carte du parcours :
    // sans mention de l'origine, deux étapes filtrant différemment seraient indiscernables.
    this.changeDataEventStep.description = this.stepDescriptionService.changeData(selectedChangeType, fieldLabels, selectedOrigins);
  }

  protected isConfigurationValid(): boolean {
    return this.changeDataEventForm.valid;
  }

  addField(): void {
    if (this.readOnlyMode) return;

    const fieldGroup = this.createFieldFormGroup();
    this.fieldsFormArray.push(fieldGroup);
  }

  removeField(index: number): void {
    if (this.readOnlyMode || this.fieldsFormArray.length <= 1) return;
    this.fieldsFormArray.removeAt(index);
  }

  getFieldControl(index: number) {
    return this.fieldsFormArray.at(index).get('field');
  }

  private createForm(): void {
    this.changeDataEventForm = this.formBuilder.group({
      changeType: this.createFormControl(this.changeDataEventStep?.changeType || ChangeDataEventStepDTO.ChangeTypeEnum.UPDATE, [Validators.required]),
      // Aucune origine sélectionnée = aucun filtrage : le comportement des scénarios existants.
      origins: this.createFormControl(this.changeDataEventStep?.origins ?? []),
      // Vide = tous les imports de l'origine retenue déclenchent l'étape, sans restriction.
      importEtlIds: this.createFormControl(this.changeDataEventStep?.importEtlIds ?? []),
      fields: this.formBuilder.array([])
    });

    if (!this.readOnlyMode) {
      this.changeDataEventForm.valueChanges.pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        if (!this.isInitializing) {
          this.markAsChanged();
        }
      })
    }
    this.changeType?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: ChangeDataEventStepDTO.ChangeTypeEnum) => {
      if (!this.isInitializing) {
        this.rebuildFieldSelectionsForChangeType(value || ChangeDataEventStepDTO.ChangeTypeEnum.UPDATE);
      }
    });
    this.origins?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: ChangeDataEventStepDTO.OriginEnum[]) => {
      if (this.hasImportOrigin(value)) {
        this.loadImportEtlOptions();
      }
    });
  }

  private loadAvailableChangeTypes(): void {
    this.availableChangeTypes = (Object.values(ChangeDataEventStepDTO.ChangeTypeEnum) as ChangeDataEventStepDTO.ChangeTypeEnum[])
      .map(value => ({
        value,
        label: this.getChangeTypeLabel(value)
      }));
  }

  private getChangeTypeLabel(changeType: ChangeDataEventStepDTO.ChangeTypeEnum): string {
    return this.stepDescriptionService.changeTypeLabel(changeType);
  }

  private loadAvailableOrigins(): void {
    this.availableOrigins = (Object.values(ChangeDataEventStepDTO.OriginEnum) as ChangeDataEventStepDTO.OriginEnum[])
      .map(value => ({ value, label: this.getOriginLabel(value) }));
  }

  private getOriginLabel(origin: ChangeDataEventStepDTO.OriginEnum): string {
    return this.stepDescriptionService.originLabel(origin);
  }

  private hasImportOrigin(origins: ChangeDataEventStepDTO.OriginEnum[]): boolean {
    return (origins ?? []).some(origin =>
      origin === ChangeDataEventStepDTO.OriginEnum.IMPORT_MANUAL || origin === ChangeDataEventStepDTO.OriginEnum.IMPORT_SCHEDULED
    );
  }

  // Restreint la liste aux imports pertinents pour l'affilié propriétaire du scénario (this.partner) :
  // même filtre que campaign-edit.component.ts (personalDataAccessPartners), pour ne pas proposer un
  // import sans rapport avec les données de cet affilié. "all" (scénario admin, non affilié) : aucune
  // restriction.
  private loadImportEtlOptions(): void {
    if (this.importEtlOptions.length > 0 || !this.tenant) return;
    const applyFilter = (etls: EtlConf[]) => {
      const scoped = (!this.partner || this.partner === 'all')
        ? etls
        : etls.filter(etl => (etl.personalDataAccessPartners || []).includes(this.partner));
      this.importEtlOptions = scoped.map(etl => ({ label: etl.name || etl.code || etl.id, value: etl.id }));
    };
    if (this.allImportEtls !== null) {
      applyFilter(this.allImportEtls);
      return;
    }
    this.etlService.getAllEtls(this.tenant, ['IMPORT']).subscribe(etls => {
      this.allImportEtls = etls || [];
      applyFilter(this.allImportEtls);
    });
  }

  private createFieldFormGroup(initialValue: any = null): FormGroup {
    return this.formBuilder.group({
      field: this.createFormControl(initialValue, [Validators.required])
    });
  }

  private loadAvailablesFields(): void {
    this.tableService.getTablesUsingGET(this.tenant).subscribe(tables => {
      const tableConfigs = tables
        .filter(t => t.code !== 'personal_affi_auth' && !t.code.includes("tools_") && !t.code.includes("email"))
        .map((table) => {
          const tableLabel = table?.label?.trim().length > 0 ? table?.label : table.code;
          const tableFields = table.fields
            .filter(f => f.categoryField !== FieldTable.CategoryFieldEnum.TECHNICAL)
            .map((field) => ({
              name: field.code,
              label: field.label ?? field.code,
              variableType: parseType(field.categoryField, field.type),
              valueType: field.categoryField,
              schema: table.code
            } as ChangeDataSelectOption));
          return {
            label: tableLabel,
            value: table.code,
            items: tableFields,
            eventFieldForDelete: tableFields[0]?.name || 'id'
          };
        });

      this.availableFields = tableConfigs.map(tableConfig => ({
        label: tableConfig.label,
        value: tableConfig.value,
        items: tableConfig.items
      }));
      this.tableOptions = tableConfigs.map(tableConfig => ({
        name: tableConfig.value,
        label: tableConfig.label,
        variableType: 'string',
        valueType: 'TEXT',
        schema: tableConfig.value,
        eventFieldForDelete: tableConfig.eventFieldForDelete
      } as ChangeDataSelectOption));
      this.initializeFormWithExistingData()
    });
  }

  private initializeFormWithExistingData(): void {
    this.isInitializing = true;
    const initialChangeType = this.changeDataEventStep?.changeType || ChangeDataEventStepDTO.ChangeTypeEnum.UPDATE;
    this.changeType?.setValue(initialChangeType);
    const initialOrigins = this.changeDataEventStep?.origins ?? [];
    this.origins?.setValue(initialOrigins);
    this.importEtlIds?.setValue(this.changeDataEventStep?.importEtlIds ?? []);
    if (this.hasImportOrigin(initialOrigins)) {
      this.loadImportEtlOptions();
    }

    while (this.fieldsFormArray.length > 0) {
      this.fieldsFormArray.removeAt(0)
    }

    if (this.changeDataEventStep?.searchFields?.length > 0) {
      if (initialChangeType === ChangeDataEventStepDTO.ChangeTypeEnum.UPDATE) {
        this.changeDataEventStep.searchFields.forEach(searchField => {
          const matchingField = this.findFieldByTableAndCode(searchField.codeTable, searchField.codeField);
          if (matchingField) {
            const fieldGroup = this.createFieldFormGroup(matchingField);
            this.fieldsFormArray.push(fieldGroup);
          }
        });
      } else {
        const uniqueTableCodes = [...new Set(this.changeDataEventStep.searchFields.map(searchField => searchField.codeTable))];
        uniqueTableCodes.forEach(tableCode => {
          const matchingTable = this.findTableOptionByCode(tableCode);
          if (matchingTable) {
            const fieldGroup = this.createFieldFormGroup(matchingTable);
            this.fieldsFormArray.push(fieldGroup);
          }
        });
      }
    } else {
      this.addField();
    }

    this.isInitializing = false;
  }

  private findFieldByTableAndCode(tableCode: string, fieldCode: string): ChangeDataSelectOption | null {
    const group = this.availableFields.find(g => g.value === tableCode);
    if (!group) return null;

    return (group.items.find(field => field.name === fieldCode) as ChangeDataSelectOption) || null;
  }

  private findFirstFieldByTable(tableCode: string): ChangeDataSelectOption | null {
    const group = this.availableFields.find(g => g.value === tableCode);
    if (!group || group.items.length === 0) return null;
    return group.items[0] as ChangeDataSelectOption;
  }

  private findTableOptionByCode(tableCode: string): ChangeDataSelectOption | null {
    return this.tableOptions.find(table => table.schema === tableCode) || null;
  }

  private toFieldTableStep(field: ChangeDataSelectOption, changeType: ChangeDataEventStepDTO.ChangeTypeEnum) {
    if (changeType === ChangeDataEventStepDTO.ChangeTypeEnum.ADD) {
      return {
        codeTable: field.schema,
        codeField: 'id'
      };
    }
    if (changeType === ChangeDataEventStepDTO.ChangeTypeEnum.DELETE) {
      return {
        codeTable: field.schema,
        codeField: field.eventFieldForDelete || field.name || 'id'
      };
    }
    return {
      codeTable: field.schema,
      codeField: field.name
    };
  }

  private toDescriptionLabel(field: ChangeDataSelectOption, changeType: ChangeDataEventStepDTO.ChangeTypeEnum): string {
    const tableLabel = this.getTableLabel(field.schema);
    if (changeType === ChangeDataEventStepDTO.ChangeTypeEnum.UPDATE) {
      return `${field.label || field.name} (${tableLabel})`;
    }
    return tableLabel;
  }

  private getTableLabel(tableCode: string): string {
    const group = this.availableFields.find(g => g.value === tableCode);
    return group?.label || tableCode;
  }

  private rebuildFieldSelectionsForChangeType(newChangeType: ChangeDataEventStepDTO.ChangeTypeEnum): void {
    const currentSelections = this.fieldsFormArray.controls
      .map(ctrl => ctrl.get('field')?.value as ChangeDataSelectOption)
      .filter(value => !!value);

    const uniqueTableCodes = [...new Set(currentSelections.map(selection => selection.schema))];
    const rebuiltSelections = uniqueTableCodes
      .map(tableCode => {
        if (newChangeType === ChangeDataEventStepDTO.ChangeTypeEnum.UPDATE) {
          return this.findFirstFieldByTable(tableCode);
        }
        return this.findTableOptionByCode(tableCode);
      })
      .filter(selection => !!selection);

    this.isInitializing = true;
    while (this.fieldsFormArray.length > 0) {
      this.fieldsFormArray.removeAt(0);
    }
    if (rebuiltSelections.length > 0) {
      rebuiltSelections.forEach(selection => this.fieldsFormArray.push(this.createFieldFormGroup(selection)));
    } else {
      this.addField();
    }
    this.isInitializing = false;
  }
}
